import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getShopId } from "../utils/shop";
import "../styles/Settings.css";

export default function Settings() {
  // --- EXISTING FIREBASE STATE & LOGIC ---
  const [businessName, setBusinessName] = useState(localStorage.getItem("businessName") || "Green Mart");
  const [gstin, setGstin] = useState(localStorage.getItem("gstin") || "27ABCDE1234F1Z5");
  const [phone, setPhone] = useState(localStorage.getItem("businessPhone") || "+91 98765 43210");
  const [address, setAddress] = useState(localStorage.getItem("businessAddress") || "123, MG Road, Pune, Maharashtra - 411001");
  const [shopId, setShopId] = useState("");
  const [licenseKey, setLicenseKey] = useState("");

  useEffect(() => {
    const loadShopData = async () => {
      try {
        const currentShopId = getShopId();
        if(!currentShopId) return;
        setShopId(currentShopId);
        
        const shopDoc = await getDoc(doc(db, "shops", currentShopId));
        if (shopDoc.exists()) {
          const data = shopDoc.data();
          if(data.shopName) setBusinessName(data.shopName);
          if(data.mobile) setPhone(data.mobile);
          if(data.gstin) setGstin(data.gstin);
          if(data.address) setAddress(data.address);
          if(data.licenseKey) setLicenseKey(data.licenseKey);
        }
      } catch (error) {
        console.error(error);
      }
    };
    loadShopData();
  }, []);
  
  const handleSaveBusiness = async () => {
    try {
      const currentShopId = getShopId();
      await updateDoc(doc(db, "shops", currentShopId), {
        shopName: businessName,
        mobile: phone,
        gstin,
        address,
      });
      alert("Settings Saved Successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed To Save");
    }
  };

  // --- UPDATED SIDEBAR MENU TO MATCH IMAGE ---
  const menuItems = [
    { icon: "fa-solid fa-gear", title: "General Settings", sub: "Basic app & company settings", active: true },
    { icon: "fa-regular fa-id-badge", title: "Business Profile", sub: "Manage business information", active: false },
    { icon: "fa-regular fa-file-lines", title: "Billing Settings", sub: "Invoice, POS & print preferences", active: false },
    { icon: "fa-solid fa-cube", title: "Inventory Settings", sub: "Stock, warehouse & units", active: false },
    { icon: "fa-solid fa-percent", title: "Tax Settings", sub: "GST, tax rates & preferences", active: false },
    { icon: "fa-solid fa-user-group", title: "Users & Roles", sub: "Manage users and permissions", active: false },
    { icon: "fa-regular fa-bell", title: "Notifications", sub: "Email, SMS & in-app notifications", active: false },
    { icon: "fa-regular fa-credit-card", title: "Payments", sub: "Payment methods & banks", active: false },
    { icon: "fa-solid fa-link", title: "Integrations", sub: "Third party integrations", active: false },
    { icon: "fa-solid fa-hard-drive", title: "Backup & Restore", sub: "Backup data & restore", active: false },
    { icon: "fa-solid fa-shield-halved", title: "Security", sub: "Password & security settings", active: false },
    { icon: "fa-solid fa-globe", title: "Language & Region", sub: "App language & regional format", active: false },
    { icon: "fa-solid fa-sliders", title: "Advanced Settings", sub: "Developer & advanced options", active: false },
  ];

  return (
    <div className="set-wrapper">
      <div className="set-layout-grid">
        
        {/* LEFT COLUMN: Navigation Menu - NOW IN A CARD */}
        <div className="set-menu-col">
          <h3 className="set-menu-header">Settings</h3>
          <div className="set-menu-list">
            {menuItems.map((item, index) => (
              <div className={`set-menu-item ${item.active ? 'active' : ''}`} key={index}>
                <div className="set-mi-icon"><i className={item.icon}></i></div>
                <div className="set-mi-text">
                  <strong>{item.title}</strong>
                  <span>{item.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MIDDLE COLUMN: Main Content Forms */}
        <div className="set-main-col">
          <div className="set-main-header">
            <div>
              <h2>General Settings</h2>
              <p>Manage your basic application settings.</p>
            </div>
            <button className="set-btn-primary" onClick={handleSaveBusiness}>
              <i className="fa-regular fa-floppy-disk"></i> Save Changes
            </button>
          </div>

          <div className="set-form-section">
            <h4 className="set-section-title">Localisation</h4>
            <p className="set-section-sub">Set your default language, currency and date format.</p>
            <div className="set-form-grid-4">
              <div className="set-input-group">
                <label>Language</label>
                <select defaultValue="English"><option>English</option></select>
              </div>
              <div className="set-input-group">
                <label>Currency</label>
                <select defaultValue="INR"><option value="INR">Indian Rupee (₹)</option></select>
              </div>
              <div className="set-input-group">
                <label>Date Format</label>
                <select defaultValue="1"><option value="1">DD MMM YYYY (20 May 2025)</option></select>
              </div>
              <div className="set-input-group">
                <label>Time Format</label>
                <select defaultValue="12"><option value="12">12 Hour (hh:mm AM/PM)</option></select>
              </div>
            </div>
          </div>

          <div className="set-divider"></div>

          <div className="set-form-section">
            <h4 className="set-section-title">Business Preferences</h4>
            <p className="set-section-sub">Configure basic business preferences.</p>
            <div className="set-form-grid-4">
              <div className="set-input-group">
                <label>Financial Year</label>
                <select defaultValue="1"><option value="1">01 April - 31 March</option></select>
              </div>
              <div className="set-input-group">
                <label>Default Due Days</label>
                <div className="set-input-with-suffix">
                  <input type="number" defaultValue="30" />
                  <span className="set-suffix">days</span>
                </div>
              </div>
              <div className="set-input-group">
                <label>Default Sales Account</label>
                <select defaultValue="sales"><option value="sales">Sales Account</option></select>
              </div>
              <div className="set-input-group">
                <label>Default Purchase Account</label>
                <select defaultValue="purchase"><option value="purchase">Purchase Account</option></select>
              </div>
            </div>
            
            <div className="set-form-grid-4 set-mt-20">
              <div className="set-input-group">
                <label>Round Off</label>
                <label className="set-checkbox-label">
                  <input type="checkbox" defaultChecked className="set-cb" /> Enable round off in invoices
                </label>
              </div>
              <div className="set-input-group">
                <label>Discount In Percentage</label>
                <label className="set-checkbox-label">
                  <input type="checkbox" defaultChecked className="set-cb" /> Show discount as percentage
                </label>
              </div>
              <div className="set-input-group">
                <label>Low Stock Alert</label>
                <div className="set-input-with-suffix">
                  <input type="number" defaultValue="10" />
                  <span className="set-suffix">items</span>
                </div>
              </div>
              <div className="set-input-group">
                <label>Allow Negative Stock</label>
                <label className="set-checkbox-label">
                  <input type="checkbox" className="set-cb" /> Allow negative stock
                </label>
              </div>
            </div>
          </div>

          <div className="set-divider"></div>

          <div className="set-form-section">
            <h4 className="set-section-title">Document Settings</h4>
            <p className="set-section-sub">Configure document numbers and prefixes.</p>
            
            <div className="set-form-grid-4">
              <div className="set-input-group">
                <label>Invoice Prefix</label>
                <input type="text" defaultValue="INV-" />
              </div>
              <div className="set-input-group">
                <label>Invoice Next Number</label>
                <input type="text" defaultValue="1001" />
              </div>
              <div className="set-input-group">
                <label>Purchase Prefix</label>
                <input type="text" defaultValue="BILL-" />
              </div>
              <div className="set-input-group">
                <label>Purchase Next Number</label>
                <input type="text" defaultValue="1001" />
              </div>
              
              <div className="set-input-group">
                <label>Quotation Prefix</label>
                <input type="text" defaultValue="QUO-" />
              </div>
              <div className="set-input-group">
                <label>Quotation Next Number</label>
                <input type="text" defaultValue="1001" />
              </div>
              <div className="set-input-group">
                <label>Credit Note Prefix</label>
                <input type="text" defaultValue="CN-" />
              </div>
              <div className="set-input-group">
                <label>Credit Note Next Number</label>
                <input type="text" defaultValue="1001" />
              </div>
            </div>
          </div>

          <div className="set-divider"></div>

          <div className="set-form-section">
            <h4 className="set-section-title">Other Preferences</h4>
            <p className="set-section-sub">Manage other general preferences.</p>
            
            <div className="set-form-grid-2">
              <div className="set-pref-item">
                <input type="checkbox" defaultChecked className="set-cb" />
                <div className="set-pref-text">
                  <strong>Enable Email Notifications</strong>
                  <span>Receive important updates on your email</span>
                </div>
              </div>
              <div className="set-pref-item">
                <input type="checkbox" defaultChecked className="set-cb" />
                <div className="set-pref-text">
                  <strong>Auto Backup</strong>
                  <span>Automatically backup your data daily</span>
                </div>
              </div>
              <div className="set-pref-item">
                <input type="checkbox" defaultChecked className="set-cb" />
                <div className="set-pref-text">
                  <strong>Show Tutorial Tips</strong>
                  <span>Show helpful tips on dashboard and modules</span>
                </div>
              </div>
              <div className="set-pref-item">
                <input type="checkbox" defaultChecked className="set-cb" />
                <div className="set-pref-text">
                  <strong>Enable Activity Log</strong>
                  <span>Track all important activities in the system</span>
                </div>
              </div>
              <div className="set-pref-item">
                <input type="checkbox" className="set-cb" />
                <div className="set-pref-text">
                  <strong>Enable SMS Notifications</strong>
                  <span>Receive important updates via SMS</span>
                </div>
              </div>
              <div className="set-pref-item">
                <input type="checkbox" className="set-cb" />
                <div className="set-pref-text">
                  <strong>Maintain Audit Trail</strong>
                  <span>Track changes for transactions and master data</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: Widgets */}
        <div className="set-widgets-col">
          
          {/* Company Info Widget */}
          <div className="set-widget-card">
            <div className="set-widget-header">
              <h4>Company Information</h4>
              <a href="#" className="set-link">Edit</a>
            </div>
            <div className="set-company-head">
              <div className="set-co-icon"><i className="fa-solid fa-store"></i></div>
              <div className="set-co-title">
                <strong>{businessName || "Green Mart"}</strong>
                <span>Retail & Wholesale Business</span>
              </div>
            </div>
            <ul className="set-co-list">
              <li><i className="fa-solid fa-file-invoice"></i> <span className="set-label-text">GSTIN:</span> <strong>{gstin || "Not Provided"}</strong></li>
              <li><i className="fa-solid fa-phone"></i> <span className="set-label-text">Phone:</span> <strong>{phone || "Not Provided"}</strong></li>
              <li><i className="fa-regular fa-envelope"></i> <span className="set-label-text">Email:</span> <strong>contact@greenmart.com</strong></li>
              <li className="set-align-top"><i className="fa-solid fa-location-dot" style={{marginTop:'3px'}}></i> <span className="set-label-text">Address:</span> <strong className="set-multi-line">{address || "Not Provided"}</strong></li>
            </ul>
          </div>

          {/* Subscription Widget */}
          <div className="set-widget-card">
            <div className="set-widget-header">
              <h4>Subscription Details</h4>
              <a href="#" className="set-link">Manage</a>
            </div>
            <ul className="set-sub-list">
              <li><span className="set-label-text">Plan</span> <strong>Business Plan</strong></li>
              <li><span className="set-label-text">Status</span> <span className="set-badge-active">Active</span></li>
              <li><span className="set-label-text">License Key</span> <strong>{licenseKey || "TRIAL-MODE"}</strong></li>
              <li><span className="set-label-text">Valid Till</span> <strong>20 Jun 2025 <span className="set-text-muted">(28 days left)</span></strong></li>
            </ul>
            <a href="#" className="set-link-center">View Subscription</a>
          </div>

          {/* Quick Actions Widget */}
          <div className="set-widget-card">
            <h4 className="set-widget-title">Quick Actions</h4>
            <div className="set-qa-grid">
              <div className="set-qa-item">
                <div className="set-qa-icon"><i className="fa-solid fa-cloud-arrow-up"></i></div>
                <div><strong>Backup Now</strong><span>Take a manual backup</span></div>
              </div>
              <div className="set-qa-item">
                <div className="set-qa-icon"><i className="fa-solid fa-broom"></i></div>
                <div><strong>Clear Cache</strong><span>Clear application cache</span></div>
              </div>
              <div className="set-qa-item">
                <div className="set-qa-icon"><i className="fa-solid fa-clock-rotate-left"></i></div>
                <div><strong>Restore Data</strong><span>Restore from backup</span></div>
              </div>
              <div className="set-qa-item">
                <div className="set-qa-icon"><i className="fa-solid fa-file-export"></i></div>
                <div><strong>Export Data</strong><span>Export data to file</span></div>
              </div>
            </div>
          </div>

          {/* Recent Activity Widget */}
          <div className="set-widget-card">
            <div className="set-widget-header">
              <h4>Recent Activity</h4>
              <a href="#" className="set-link">View All</a>
            </div>
            <div className="set-activity-list">
              <div className="set-act-item">
                <span className="set-act-dot set-bg-green"></span>
                <div className="set-act-text"><strong>Settings updated by Admin</strong><span>General settings changed</span></div>
                <div className="set-act-time">2 mins ago</div>
              </div>
              <div className="set-act-item">
                <span className="set-act-dot set-bg-blue"></span>
                <div className="set-act-text"><strong>User role updated</strong><span>New role added: Accountant</span></div>
                <div className="set-act-time">1 hour ago</div>
              </div>
              <div className="set-act-item">
                <span className="set-act-dot set-bg-orange"></span>
                <div className="set-act-text"><strong>Backup completed</strong><span>Automatic backup completed</span></div>
                <div className="set-act-time">Today, 01:30 AM</div>
              </div>
              <div className="set-act-item">
                <span className="set-act-dot set-bg-purple"></span>
                <div className="set-act-text"><strong>Company profile updated</strong><span>Business address changed</span></div>
                <div className="set-act-time">Yesterday</div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};